// Die Warteliste der Tour — was ist erledigt, was kommt noch, in welcher Reihenfolge.
//
// Vorbild ist die Warteliste in Spotify: oben steht, was gerade läuft, darunter
// "Als Nächstes" mit einem Ziehgriff pro Zeile, und Gespieltes ist weg bzw.
// zusammengeklappt. Übernommen sind das Prinzip und die Handgriffe, nicht das
// Aussehen — hier steht kein Cover, sondern Nummer, Name und Straße, und eine
// Zeile ist ein Kunde, der auf seine Eier wartet.
//
// Drei Dinge, die der Fahrer unterwegs braucht:
//   1. Was habe ich schon abgefahren, was fehlt noch?     → drei Abschnitte
//   2. Reihenfolge ändern (Baustelle, Kunde nicht da)     → Ziehgriff
//   3. Kunde dazu oder weg                                → Suche / ✕
//
// ══ Warum von Hand gezogen und nicht mit einer Bibliothek ══
//
// Die App hat keine Drag-and-Drop-Abhängigkeit, und die üblichen Kandidaten
// bringen entweder kein sauberes Touch-Verhalten mit oder 30 kB für eine Liste.
// Gezogen wird ausschliesslich am Griff (`touch-action: none` NUR dort) — der
// Rest der Zeile bleibt scrollbar. Zöge die ganze Zeile, könnte man die Liste
// mit 200 Stopps nicht mehr durchblättern, ohne ständig etwas zu verschieben.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  useKundeZurTour,
  useKundeVonTour,
  useStopsUmsortieren,
  type TourStop,
} from "@/lib/tour";
import { useCustomerList } from "@/lib/crm";
import { Button, Input, Sheet, cn } from "@/design-system/ui";

/**
 * Abstand von Zeilenanfang zu Zeilenanfang in px: `h-[68px]` + `mb-1` (4px).
 * Die Ziel-Berechnung beim Ziehen rechnet damit — wer die Höhe ändert, muss
 * diese Zahl mitändern, sonst wandert die Zeile beim Ziehen aus dem Tritt.
 */
const ZEILE = 72;

/** Wie nah am Rand beginnt die Liste von selbst zu rollen? */
const RAND = 72;

type Tour = { id: string; status: string; stops: TourStop[] };

export function Warteliste({
  open,
  onClose,
  tour,
  date,
}: {
  open: boolean;
  onClose: () => void;
  tour: Tour;
  date: string;
}) {
  const [suchModus, setSuchModus] = useState(false);
  const [suche, setSuche] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [erledigteOffen, setErledigteOffen] = useState(false);

  const hinzufuegen = useKundeZurTour(date);
  const entfernen = useKundeVonTour(date);
  const umsortieren = useStopsUmsortieren(date);
  const { data: treffer = [] } = useCustomerList(suchModus ? suche : "");

  // ── Die drei Abschnitte
  const offen = tour.stops.filter((s) => s.status === "pending");
  const unterwegs = tour.stops.find((s) => s.status === "arrived") ?? null;
  const erledigt = tour.stops.filter((s) => s.status !== "pending" && s.status !== "arrived");

  // "Jetzt" ist entweder der Stop, an dem der Fahrer schon steht (arrived), oder
  // schlicht der nächste offene. Er hängt oben fest und wird nicht gezogen —
  // wie der laufende Titel in der Warteliste.
  const jetzt = unterwegs ?? offen[0] ?? null;
  const wartend = unterwegs ? offen : offen.slice(1);

  // ── Die gezogene Reihenfolge lebt lokal, damit die Liste beim Ziehen nicht ruckelt.
  const [ordnung, setOrdnung] = useState<string[]>(() => wartend.map((s) => s.id));
  const wartendIds = wartend.map((s) => s.id).join(",");
  const ziehtGerade = useRef(false);
  useEffect(() => {
    // Während des Ziehens NICHT übernehmen: Käme in dem Moment eine Antwort vom
    // Server herein, spränge die Zeile unter dem Finger weg.
    if (!ziehtGerade.current) setOrdnung(wartendIds ? wartendIds.split(",") : []);
  }, [wartendIds]);

  const nachId = new Map(tour.stops.map((s) => [s.id, s]));
  const liste = ordnung.map((id) => nachId.get(id)).filter(Boolean) as TourStop[];

  const speichern = useCallback(
    (neu: string[]) => {
      setFehler(null);
      // Der "Jetzt"-Stop gehört mit in die Liste, wenn er noch aussteht — die
      // Datenbank-Funktion erwartet ALLE offenen Stopps, sonst lehnt sie ab.
      const alle = jetzt && jetzt.status === "pending" ? [jetzt.id, ...neu] : neu;
      umsortieren.mutate(
        { tourId: tour.id, stopIds: alle },
        { onError: (e) => setFehler((e as Error).message) }
      );
    },
    [jetzt, tour.id, umsortieren]
  );

  const zufuegen = async (customerId: string) => {
    setFehler(null);
    try {
      await hinzufuegen.mutateAsync({ tourId: tour.id, customerId });
      setSuchModus(false);
      setSuche("");
    } catch (e) {
      setFehler((e as Error).message);
    }
  };

  const wegnehmen = async (stopId: string) => {
    setFehler(null);
    try {
      await entfernen.mutateAsync(stopId);
    } catch (e) {
      setFehler((e as Error).message);
    }
  };

  const gesamt = tour.stops.length;
  const fertig = erledigt.length;

  // Wer schon auf der Tour ist, taucht in der Suche nicht auf — niemand doppelt,
  // und die Trefferliste bleibt kurz.
  const schonDrauf = new Set(tour.stops.map((s) => s.order.customer.id));
  const vorschlaege = treffer.filter((k) => !schonDrauf.has(k.id)).slice(0, 20);

  return (
    <Sheet
      open={open}
      onClose={() => {
        setSuchModus(false);
        setSuche("");
        setFehler(null);
        onClose();
      }}
      title={suchModus ? "Kunde hinzufügen" : "Tour"}
      footer={
        suchModus ? undefined : (
          <Button variant="subtle" className="w-full" onClick={() => setSuchModus(true)}>
            + Kunde hinzufügen
          </Button>
        )
      }
    >
      {fehler && (
        <p className="mb-3 rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
          {fehler}
        </p>
      )}

      {suchModus ? (
        <div className="space-y-3">
          <Input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Name oder Straße suchen"
            autoFocus
          />
          {suche.trim().length < 2 ? (
            <p className="py-6 text-center text-sm text-ink-500">
              Mindestens zwei Buchstaben eingeben.
            </p>
          ) : vorschlaege.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-500">Kein passender Kunde.</p>
          ) : (
            <ul className="space-y-1.5">
              {vorschlaege.map((k) => (
                <li key={k.id}>
                  <button
                    onClick={() => zufuegen(k.id)}
                    disabled={hinzufuegen.isPending}
                    className="flex w-full items-center gap-3 rounded-xl border border-ink-700 bg-ink-900 px-3.5 py-3 text-left transition-colors duration-fast hover:bg-ink-850 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60 disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-medium text-ink-100">
                        {k.display_name}
                      </span>
                      <span className="block truncate text-sm text-ink-400">
                        {k.addresses?.[0]?.street ?? "—"}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-medium text-gruen-400">+ hinzufügen</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button variant="ghost" size="sm" className="w-full" onClick={() => setSuchModus(false)}>
            Zurück zur Liste
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <Fortschritt fertig={fertig} gesamt={gesamt} offen={offen.length} />

          {jetzt && (
            <section>
              <Abschnitt>{unterwegs ? "Angekommen" : "Jetzt"}</Abschnitt>
              <JetztZeile stop={jetzt} />
            </section>
          )}

          <section>
            <Abschnitt>
              Als Nächstes{liste.length > 0 && ` (${liste.length})`}
            </Abschnitt>
            {liste.length === 0 ? (
              <p className="px-1 py-3 text-sm text-ink-500">
                {jetzt
                  ? "Danach ist Feierabend."
                  : gesamt === 0
                    ? "Noch kein Kunde auf dieser Tour — unten hinzufügen."
                    : "Keine offenen Stopps mehr."}
              </p>
            ) : (
              <ZiehListe
                stops={liste}
                ziehtGerade={ziehtGerade}
                onNeueOrdnung={(neu) => {
                  setOrdnung(neu);
                  speichern(neu);
                }}
                onEntfernen={wegnehmen}
                entfernenLaeuft={entfernen.isPending}
              />
            )}
          </section>

          {erledigt.length > 0 && (
            <section>
              <button
                onClick={() => setErledigteOffen((v) => !v)}
                className="flex w-full items-center gap-2 px-1 py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60"
              >
                <span className="text-xs font-medium uppercase tracking-wider text-ink-400">
                  Erledigt ({erledigt.length})
                </span>
                <span
                  className={cn(
                    "text-ink-500 transition-transform duration-fast",
                    erledigteOffen && "rotate-90"
                  )}
                  aria-hidden
                >
                  ›
                </span>
              </button>
              {erledigteOffen && (
                <ul className="space-y-1">
                  {erledigt.map((s) => (
                    <li
                      key={s.id}
                      className="flex h-[68px] items-center gap-3 rounded-xl border border-ink-800 bg-ink-900/40 px-3.5"
                    >
                      <span className="w-7 shrink-0 text-center text-xs tabular-nums text-ink-600">
                        {s.seq}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] text-ink-400">
                          {s.order.customer.display_name}
                        </span>
                        <span className="block truncate text-sm text-ink-600">
                          {s.order.address.street}
                        </span>
                      </span>
                      <StatusMarke status={s.status} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      )}
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────── Bausteine

function Abschnitt({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-1.5 px-1 text-xs font-medium uppercase tracking-wider text-ink-400">
      {children}
    </h3>
  );
}

/** Ein Balken sagt in einem Blick, wie weit der Tag ist — eine Zahl allein nicht. */
function Fortschritt({ fertig, gesamt, offen }: { fertig: number; gesamt: number; offen: number }) {
  const anteil = gesamt ? Math.round((fertig / gesamt) * 100) : 0;
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-[15px] text-ink-200">
          <span className="font-semibold tabular-nums text-ink-50">{fertig}</span> von{" "}
          <span className="tabular-nums">{gesamt}</span> erledigt
        </span>
        <span className="text-sm tabular-nums text-ink-400">{offen} offen</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-ink-800">
        <div
          className="h-full rounded-full bg-gruen-500 transition-[width] duration-300"
          style={{ width: `${anteil}%` }}
        />
      </div>
    </div>
  );
}

function JetztZeile({ stop }: { stop: TourStop }) {
  return (
    <div className="flex h-[68px] items-center gap-3 rounded-xl border border-gruen-500/40 bg-gruen-500/10 px-3.5">
      <span className="w-7 shrink-0 text-center text-xs font-semibold tabular-nums text-gruen-400">
        {stop.seq}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-medium text-ink-50">
          {stop.order.customer.display_name}
        </span>
        <span className="block truncate text-sm text-ink-300">{stop.order.address.street}</span>
      </span>
      {stop.order.customer.preferred_time && (
        <span className="shrink-0 rounded bg-gruen-500/20 px-2 py-0.5 text-sm font-medium tabular-nums text-gruen-400">
          {stop.order.customer.preferred_time.slice(0, 5)}
        </span>
      )}
    </div>
  );
}

function StatusMarke({ status }: { status: TourStop["status"] }) {
  const text =
    status === "delivered" ? "geliefert" : status === "absent" ? "nicht da" : status;
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2.5 py-1 text-xs",
        status === "delivered" ? "bg-gruen-500/15 text-gruen-400" : "bg-ink-800 text-ink-400"
      )}
    >
      {text}
    </span>
  );
}

// ─────────────────────────────────────────────────────── Ziehen & Sortieren

/**
 * Die verschiebbare Liste.
 *
 * Gezogen wird am Griff rechts. Während des Ziehens werden die anderen Zeilen
 * nur VERSCHOBEN (transform), nicht neu gerendert — dadurch bleibt die Liste
 * auch mit 200 Stopps flüssig, und die Zeile unter dem Finger springt nicht.
 * Erst beim Loslassen ändert sich die tatsächliche Reihenfolge.
 */
function ZiehListe({
  stops,
  ziehtGerade,
  onNeueOrdnung,
  onEntfernen,
  entfernenLaeuft,
}: {
  stops: TourStop[];
  ziehtGerade: React.MutableRefObject<boolean>;
  onNeueOrdnung: (ids: string[]) => void;
  onEntfernen: (stopId: string) => void;
  entfernenLaeuft: boolean;
}) {
  const listeRef = useRef<HTMLUListElement>(null);
  const [zug, setZug] = useState<{ von: number; ziel: number; dy: number } | null>(null);

  // Alles, was der Bewegungs-Handler braucht, ohne bei jeder Regung neu zu binden.
  const griff = useRef({
    von: 0,
    startY: 0,
    startScroll: 0,
    letzteY: 0,
    scroller: null as HTMLElement | null,
    rollen: 0,
  });

  const beenden = useCallback(() => {
    cancelAnimationFrame(griff.current.rollen);
    griff.current.rollen = 0;
  }, []);

  useEffect(() => beenden, [beenden]);

  const neuBerechnen = useCallback(() => {
    const g = griff.current;
    const scrollDelta = (g.scroller?.scrollTop ?? 0) - g.startScroll;
    const dy = g.letzteY - g.startY + scrollDelta;
    const ziel = Math.min(
      stops.length - 1,
      Math.max(0, g.von + Math.round(dy / ZEILE))
    );
    setZug({ von: g.von, ziel, dy });
  }, [stops.length]);

  const rollenPruefen = useCallback(() => {
    const g = griff.current;
    if (!g.scroller) return;
    const kasten = g.scroller.getBoundingClientRect();
    const obenRein = g.letzteY - kasten.top;
    const untenRein = kasten.bottom - g.letzteY;
    let schritt = 0;
    if (obenRein < RAND) schritt = -Math.ceil((RAND - obenRein) / 6);
    else if (untenRein < RAND) schritt = Math.ceil((RAND - untenRein) / 6);

    if (schritt !== 0) {
      g.scroller.scrollTop += schritt;
      neuBerechnen();
    }
    g.rollen = requestAnimationFrame(rollenPruefen);
  }, [neuBerechnen]);

  const anfassen = (index: number) => (e: React.PointerEvent) => {
    // Ohne das startet iOS aus derselben Geste noch ein Textmarkieren.
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

    const g = griff.current;
    g.von = index;
    g.startY = e.clientY;
    g.letzteY = e.clientY;
    g.scroller = scrollElternteil(listeRef.current);
    g.startScroll = g.scroller?.scrollTop ?? 0;

    ziehtGerade.current = true;
    setZug({ von: index, ziel: index, dy: 0 });
    navigator.vibrate?.(8); // kurzes "aufgenommen" — mit Handschuhen sieht man wenig
    g.rollen = requestAnimationFrame(rollenPruefen);
  };

  const bewegen = (e: React.PointerEvent) => {
    if (!ziehtGerade.current) return;
    griff.current.letzteY = e.clientY;
    neuBerechnen();
  };

  const loslassen = (e: React.PointerEvent) => {
    if (!ziehtGerade.current) return;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    beenden();
    ziehtGerade.current = false;

    const z = zug;
    setZug(null);
    if (!z || z.ziel === z.von) return;

    const neu = stops.map((s) => s.id);
    const [weg] = neu.splice(z.von, 1);
    neu.splice(z.ziel, 0, weg);
    navigator.vibrate?.(12);
    onNeueOrdnung(neu);
  };

  return (
    <ul ref={listeRef} className="relative select-none">
      {stops.map((s, i) => {
        // Wohin rutscht diese Zeile, während eine andere über ihr schwebt?
        let versatz = 0;
        if (zug) {
          if (i === zug.von) versatz = zug.dy;
          else if (zug.von < i && i <= zug.ziel) versatz = -ZEILE;
          else if (zug.ziel <= i && i < zug.von) versatz = ZEILE;
        }
        const gezogen = zug?.von === i;

        return (
          <li
            key={s.id}
            style={{ transform: versatz ? `translateY(${versatz}px)` : undefined }}
            className={cn(
              "flex h-[68px] items-center gap-3 rounded-xl border px-3.5",
              // 1px Abstand über die Höhe hinaus wäre eine zweite Rechengröße —
              // deshalb sitzt der Abstand als Rand INNEN, und ZEILE bleibt 68.
              "mb-1 border-ink-700/70 bg-ink-900",
              gezogen
                ? "relative z-10 border-gruen-500/50 shadow-lg shadow-black/40"
                : zug
                  ? "transition-transform duration-fast"
                  : ""
            )}
          >
            <span className="w-7 shrink-0 text-center text-xs tabular-nums text-ink-500">
              {s.seq}
            </span>

            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] text-ink-100">
                {s.order.customer.display_name}
              </span>
              <span className="block truncate text-sm text-ink-400">
                {s.order.address.street}
              </span>
            </span>

            {s.order.customer.preferred_time && (
              <span className="shrink-0 rounded bg-ink-800 px-2 py-0.5 text-sm tabular-nums text-ink-300">
                {s.order.customer.preferred_time.slice(0, 5)}
              </span>
            )}

            <button
              onClick={() => onEntfernen(s.id)}
              disabled={entfernenLaeuft}
              aria-label={`${s.order.customer.display_name} von der Tour entfernen`}
              className="flex h-11 w-9 shrink-0 items-center justify-center rounded-lg text-ink-500 transition-colors duration-fast hover:bg-danger-bg/10 hover:text-danger-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60 disabled:opacity-40"
            >
              ✕
            </button>

            {/* Der Griff. `touch-action: none` NUR hier — sonst liesse sich die
                Liste nicht mehr scrollen. */}
            <span
              onPointerDown={anfassen(i)}
              onPointerMove={bewegen}
              onPointerUp={loslassen}
              onPointerCancel={loslassen}
              role="button"
              tabIndex={-1}
              aria-label={`${s.order.customer.display_name} verschieben`}
              style={{ touchAction: "none" }}
              className="flex h-11 w-9 shrink-0 cursor-grab items-center justify-center rounded-lg text-ink-500 hover:text-ink-200 active:cursor-grabbing"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                <path d="M4 8h16M4 12h16M4 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Der nächste Vorfahr, in dem wirklich gescrollt wird (das Blatt selbst). */
function scrollElternteil(el: HTMLElement | null): HTMLElement | null {
  let p = el?.parentElement ?? null;
  while (p) {
    const s = getComputedStyle(p).overflowY;
    if ((s === "auto" || s === "scroll") && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return null;
}
